import { Grid, Box, IconButton, Typography } from "@mui/material";
import { Move, MOVE_DAMAGE_CLASS_PHYSICAL, MOVE_DAMAGE_CLASS_SPECIAL } from "../../move/move";
import { PokemonV2 } from "../../pokemon/pokemon";
import type { AttackPokemonData } from "../../types/attack";
import type { PokemonOption } from "../../types/pokemon";
import type { ItemOption } from "../../types/item";
import { retCalc } from "../../types/damage";
import type { otherSetting } from "../../types/other-setting";
import type { MoveDamageSetting } from "../../types/damage-setting";
import DefencePokemonSelect from "./DefencePokemonSelect";
import AttackModeDisplay from "./AttackModeDisplay";
import DefencePokemonStatInputAttackMode from "./DefencePokemonStatInputAttackMode";
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { POKEMON_ITEM_ID } from "../../types/item";
import AttackPokemonCardFrame from "../../components/AttackPokemonCardFrame";
import AttackPokemonItemInput from "../../components/AttackPokemonItemInput";
import AttackPokemonAbilityInput from "../../components/AttackPokemonAbilityInput";
import AttackPokemonTeraSelector from "../../components/AttackPokemonTeraSelector";
import { getArceusEffectiveType } from "../../pokemon/PlateUtils";

function OffenceMode({
  selectedPokemon,
  pokemonOptions,
  attackPokemonList,
  handleAddAttackPokemon,
  getMoveDisplayName,
  getMoveDisplayPower,
  handleRemoveAttackPokemon,
  handleCopyAttackPokemon,
  calculateAttackModeDamage,
  getAttackModeDamageDetail,
  currentOtherSetting,
  handleSelectTerasuTypeForAttackPokemon,
  handleSelectItemForAttackPokemon,
  handleSelectAbilityForAttackPokemon,
  handleUpdateKodaiQuarkStat,
  getOffenseItems,
  handleUpdateAttackPokemonStats,
  handleUpdateAttackPokemonPersonality,
  handleUpdateAttackPokemonRank
}: {
  selectedPokemon: PokemonV2 | null;
  pokemonOptions: PokemonOption[];
  attackPokemonList: AttackPokemonData[];
  handleAddAttackPokemon: (event: any, newValue: any) => void;
  getMoveDisplayName: (move: Move, pokemonId: number) => string;
  getMoveDisplayPower: (move: Move, pokemonId: number) => number | string;
  handleRemoveAttackPokemon: (id: string) => void;
  handleCopyAttackPokemon: (id: string) => void;
  calculateAttackModeDamage: (
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ) => string[];
  getAttackModeDamageDetail: (
    attackerPokemon: PokemonV2,
    targetPokemon: AttackPokemonData,
    overrideOtherSetting?: otherSetting,
    resolveMoveSetting?: (moveId: number) => MoveDamageSetting | undefined,
  ) => retCalc[];
  currentOtherSetting: otherSetting;
  handleSelectTerasuTypeForAttackPokemon: (id: string, terasuType: number) => void;
  handleSelectItemForAttackPokemon: (id: string, itemId: POKEMON_ITEM_ID) => void;
  handleSelectAbilityForAttackPokemon: (id: string, abilityId: number) => void;
  handleUpdateKodaiQuarkStat: (id: string, value: 'attack' | 'special_attack') => void;
  getOffenseItems: ItemOption[];
  handleUpdateAttackPokemonStats: (id: string, field: string, value: number) => void;
  handleUpdateAttackPokemonPersonality: (id: string, field: string, value: number) => void;
  handleUpdateAttackPokemonRank: (id: string, field: string, value: number) => void;
}) {
  return <>
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <DefencePokemonSelect
        pokemonOptions={pokemonOptions}
        handleAddAttackPokemon={handleAddAttackPokemon}
      />
      {/* 攻撃モードでは技選択機能を無効化 */}
    </Grid>

    {attackPokemonList.length > 0 ? (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        overflowY: 'auto',
        minHeight: 0
      }}>
        {attackPokemonList.map((attackPokemon) => {

          // 攻撃モードでは背景色を白に統一
          let backgroundColor = 'white';
          // プレートやフォルム差分を加味した表示用タイプを取得する
          const pokemonTypeIdList = getArceusEffectiveType({
            id: attackPokemon.pokemon.id,
            species_id: attackPokemon.pokemon.species_id,
            type_id_list: attackPokemon.pokemon.type_id_list,
            selected_item_id: attackPokemon.selectedItemId ?? attackPokemon.pokemon.selected_item_id,
          });

          return (
            <AttackPokemonCardFrame
              key={attackPokemon.id}
              pokemonId={attackPokemon.pokemon.id}
              pokemonName={attackPokemon.pokemon.name_ja}
              pictureUrl={attackPokemon.pokemon.picture_url}
              pokemonTypeIdList={pokemonTypeIdList}
              backgroundColor={backgroundColor}
              border="1px solid #e0e0e0"
              cursor="default"
              actions={
                <>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyAttackPokemon(attackPokemon.id)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        color: 'primary.dark',
                        backgroundColor: 'primary.light'
                      }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttackPokemon(attackPokemon.id)}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        color: 'error.dark',
                        backgroundColor: 'error.light'
                      }
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              }
            >
              {selectedPokemon &&
              selectedPokemon.selected_moves &&
              selectedPokemon.selected_moves.some(
                (move) =>
                  move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
                  move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL
              ) ? (
                <Box sx={{ flex: 2, display: 'flex', gap: 0.05, minWidth: 300 }}>
                  <AttackModeDisplay
                    leftSidePokemon={selectedPokemon}
                    rightSideTargetPokemon={attackPokemon}
                    otherSetting={currentOtherSetting}
                    calculateAttackModeDamage={calculateAttackModeDamage}
                    getAttackModeDamageDetail={getAttackModeDamageDetail}
                    getMoveDisplayName={getMoveDisplayName}
                    getMoveDisplayPower={getMoveDisplayPower}
                  />
                </Box>
              ) : null}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <AttackPokemonItemInput
                  attackPokemon={attackPokemon}
                  handleSelectItemForAttackPokemon={handleSelectItemForAttackPokemon}
                  getOffenseItems={getOffenseItems}
                />
                <AttackPokemonAbilityInput
                  attackPokemon={attackPokemon}
                  handleSelectAbilityForAttackPokemon={handleSelectAbilityForAttackPokemon}
                  handleUpdateKodaiQuarkStat={handleUpdateKodaiQuarkStat}
                />
                <AttackPokemonTeraSelector
                  variant="simple"
                  attackPokemon={attackPokemon}
                  handleSelectTerasuTypeForAttackPokemon={handleSelectTerasuTypeForAttackPokemon}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <DefencePokemonStatInputAttackMode
                  attackPokemon={attackPokemon}
                  handleUpdateAttackPokemonStats={handleUpdateAttackPokemonStats}
                  handleUpdateAttackPokemonPersonality={handleUpdateAttackPokemonPersonality}
                  handleUpdateAttackPokemonRank={handleUpdateAttackPokemonRank}
                />
              </Box>
            </AttackPokemonCardFrame>
          );
        })}
      </Box>
    ) : (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        flexDirection: 'column',
        minHeight: '200px'
      }}>
        <Typography variant="h6" color="text.secondary" textAlign="center">
          攻撃ポケモンを追加してください
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
          上記から攻撃ポケモンを選択して追加できます
        </Typography>
      </Box>
    )}



  </>
}
export default OffenceMode
